import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { 
  Droplets, 
  Zap, 
  Sparkles, 
  Scale, 
  Camera, 
  FileText, 
  Trash2, 
  Edit3, 
  Maximize2,
  Calendar,
  Clock,
  ChevronRight
} from 'lucide-react';
import { Entry, EntryType, MediaAttachment, CustomFieldDefinition } from '../types';

interface TimelineViewProps {
  entries: Entry[];
  customFields?: CustomFieldDefinition[];
  onDeleteEntry: (id: string) => void;
  onEditEntry: (entry: Entry) => void;
}

const getCategoryBadge = (type: EntryType) => {
  switch (type) {
    case 'watering':
      return { badgeClass: 'badge-cyan', icon: <Droplets size={13} />, label: 'Watering' };
    case 'feeding':
      return { badgeClass: 'badge-purple', icon: <Sparkles size={13} />, label: 'Feeding / Nutrients' };
    case 'light':
      return { badgeClass: 'badge-amber', icon: <Zap size={13} />, label: 'Light Cycle' };
    case 'yield':
      return { badgeClass: 'badge-emerald', icon: <Scale size={13} />, label: 'Harvest & Yield' };
    case 'milestone':
      return { badgeClass: 'badge-rose', icon: <Sparkles size={13} />, label: 'Milestone' };
    case 'photo':
      return { badgeClass: 'badge-cyan', icon: <Camera size={13} />, label: 'Photo Log' };
    default:
      return { badgeClass: 'badge-emerald', icon: <FileText size={13} />, label: 'General Log' };
  }
};

export const TimelineView: React.FC<TimelineViewProps> = ({
  entries,
  customFields = [],
  onDeleteEntry,
  onEditEntry,
}) => {
  const [filterType, setFilterType] = useState<string>('all');
  const [activeMedia, setActiveMedia] = useState<MediaAttachment | null>(null);

  const filteredEntries = entries.filter((e) => {
    if (filterType === 'all') return true;
    return e.category_type === filterType;
  });

  return (
    <div>
      {/* Category Filter Pills */}
      <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.75rem', marginBottom: '1.25rem' }}>
        {[
          { id: 'all', label: 'All Entries' },
          { id: 'watering', label: 'Watering' },
          { id: 'feeding', label: 'Feeding' },
          { id: 'light', label: 'Light Time' },
          { id: 'yield', label: 'Yield / Harvest' },
          { id: 'milestone', label: 'Milestones' },
          { id: 'photo', label: 'Photos' },
        ].map((item) => (
          <button
            key={item.id}
            className={`btn btn-sm ${filterType === item.id ? 'btn-primary' : 'btn-secondary'}`}
            style={{ borderRadius: 'var(--radius-full)', whiteSpace: 'nowrap' }}
            onClick={() => setFilterType(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {filteredEntries.length === 0 ? (
        <div className="glass-panel" style={{ textAlign: 'center', padding: '3.5rem 1.5rem', color: 'var(--text-sub)' }}>
          <FileText size={36} style={{ margin: '0 auto 1rem', opacity: 0.4 }} />
          <h3 style={{ fontSize: '1.2rem', color: 'var(--text-main)', marginBottom: '0.5rem' }}>No entries found</h3>
          <p style={{ fontSize: '0.9rem' }}>
            {filterType === 'all'
              ? 'Start by logging your first watering, light check, or plant milestone.'
              : `No ${filterType} entries recorded yet.`}
          </p>
        </div>
      ) : (
        <div>
          {filteredEntries.map((entry) => {
            const badgeInfo = getCategoryBadge(entry.category_type);
            const entryDate = parseISO(entry.entry_date);

            return (
              <div key={entry.id} className="glass-panel timeline-card">
                {/* Header */}
                <div className="timeline-card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                    <span className={`badge ${badgeInfo.badgeClass}`}>
                      {badgeInfo.icon}
                      <span>{badgeInfo.label}</span>
                    </span>
                    <h3 style={{ fontSize: '1.15rem', color: 'var(--text-main)' }}>{entry.title}</h3>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: 'var(--text-sub)', fontSize: '0.8rem' }}>
                      <Calendar size={13} />
                      <span>{format(entryDate, 'MMM d, yyyy')}</span>
                      <Clock size={13} style={{ marginLeft: 6 }} />
                      <span>{format(entryDate, 'h:mm a')}</span>
                    </div>

                    <button
                      className="btn btn-secondary btn-icon"
                      style={{ padding: '0.35rem', opacity: 0.7 }}
                      onClick={() => onEditEntry(entry)}
                      title="Edit Entry"
                    >
                      <Edit3 size={13} />
                    </button>
                    <button
                      className="btn btn-secondary btn-icon"
                      style={{ padding: '0.35rem', opacity: 0.7, color: 'var(--accent-rose)' }}
                      onClick={() => {
                        if (confirm('Delete this entry?')) {
                          onDeleteEntry(entry.id);
                        }
                      }}
                      title="Delete Entry"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                {/* Content Notes */}
                {entry.content && (
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.6, whiteSpace: 'pre-line', marginBottom: '0.75rem' }}>
                    {entry.content}
                  </p>
                )}

                {/* Custom Fields Badges */}
                {entry.custom_fields && Object.keys(entry.custom_fields).length > 0 && (
                  <div className="custom-metrics-row">
                    {Object.entries(entry.custom_fields).map(([key, val]) => {
                      if (val === undefined || val === null || val === '') return null;
                      const fieldDef = customFields.find((f) => f.id === key);
                      const fieldName = fieldDef ? fieldDef.name : key.replace(/^f_/, '').replace(/_/g, ' ');
                      const unit = fieldDef?.unit ? ` ${fieldDef.unit}` : '';

                      return (
                        <div key={key} className="metric-pill">
                          <span className="metric-pill-label">{fieldName}:</span>
                          <span className="metric-pill-val">{String(val)}{unit}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Media Attachments */}
                {entry.attachments && entry.attachments.length > 0 && (
                  <div className="media-preview-grid">
                    {entry.attachments.map((media) => {
                      const isVideo = media.content_type?.startsWith('video/') || media.filename?.endsWith('.mp4');
                      return (
                        <div
                          key={media.id}
                          className="media-thumbnail"
                          onClick={() => setActiveMedia(media)}
                          title="Click to view attachment"
                        >
                          {isVideo ? (
                            <video src={media.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                          ) : (
                            <img src={media.url} alt={media.filename} loading="lazy" />
                          )}
                          <div
                            style={{
                              position: 'absolute',
                              bottom: 4,
                              right: 4,
                              background: 'rgba(0,0,0,0.6)',
                              padding: 3,
                              borderRadius: 4,
                              color: '#fff',
                            }}
                          >
                            <Maximize2 size={12} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox Modal */}
      {activeMedia && (
        <div className="lightbox-modal" onClick={() => setActiveMedia(null)}>
          <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            {activeMedia.content_type?.startsWith('video/') || activeMedia.filename?.endsWith('.mp4') ? (
              <video src={activeMedia.url} controls autoPlay className="lightbox-content" />
            ) : (
              <img src={activeMedia.url} alt={activeMedia.filename} className="lightbox-content" />
            )}
            <div style={{ textAlign: 'center', marginTop: '0.75rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              <span>{activeMedia.filename}</span>
              <button
                className="btn btn-secondary btn-sm"
                style={{ marginLeft: '1rem' }}
                onClick={() => setActiveMedia(null)}
              >
                Close (ESC)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
